import { Button, Group, Select, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { AttractionArtifactKindEnum } from '@routellers/types/dist/city-attraction/enum/attraction-artifact-kind.enum';
import { UpsertCityAttractionArtifactDto } from '@routellers/types/dist/city-attraction/upsert-city-attraction-artifact.dto';
import { IconPlus } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Section } from '../../components/editor';
import { enumOptions } from '../../i18n';

import { emptyArtifact } from './artifacts-helpers';

type Props = {
    artifacts: UpsertCityAttractionArtifactDto[];
    onChange: (next: UpsertCityAttractionArtifactDto[]) => void;
};

export const ArtifactsPanel = ({ artifacts, onChange }: Props) => {
    const { t } = useTranslation();
    const kindOptions = useMemo(
        () => enumOptions(Object.values(AttractionArtifactKindEnum), t, 'enums.artifactKind'),
        [t],
    );

    const patch = (index: number, values: Partial<UpsertCityAttractionArtifactDto>) => {
        onChange(artifacts.map((a, i) => (i === index ? { ...a, ...values } : a)));
    };

    return (
        <Stack gap='lg'>
            <Section
                title={t('editors.attraction.artifactsPanel.title')}
                description={t('editors.attraction.artifactsPanel.sectionDescription')}
            >
                <Stack gap='md'>
                    {artifacts.map((artifact, index) => (
                        <Stack
                            key={`artifact-${index}`}
                            gap='sm'
                            p='md'
                            style={{
                                border: '1px solid var(--mantine-color-gray-3)',
                                borderRadius: 'var(--mantine-radius-md)',
                            }}
                        >
                            <Group justify='space-between'>
                                <Text size='sm' fw={600}>
                                    {t('editors.attraction.artifactsPanel.artifactN', {
                                        n: index + 1,
                                    })}
                                </Text>
                                <Button
                                    size='xs'
                                    variant='subtle'
                                    color='red'
                                    onClick={() =>
                                        onChange(artifacts.filter((_, i) => i !== index))
                                    }
                                >
                                    {t('editors.attraction.artifactsPanel.remove')}
                                </Button>
                            </Group>
                            <Group grow>
                                <Select
                                    label={t('editors.attraction.artifactsPanel.kind')}
                                    data={kindOptions}
                                    value={artifact.kind}
                                    onChange={(v) =>
                                        patch(index, {
                                            kind:
                                                (v as AttractionArtifactKindEnum) ||
                                                AttractionArtifactKindEnum.OTHER,
                                        })
                                    }
                                />
                                <TextInput
                                    label={t('editors.attraction.artifactsPanel.personId')}
                                    value={
                                        artifact.personId != null ? String(artifact.personId) : ''
                                    }
                                    onChange={(e) =>
                                        patch(index, {
                                            personId: e.currentTarget.value.trim()
                                                ? Number(e.currentTarget.value)
                                                : null,
                                        })
                                    }
                                />
                            </Group>
                            <TextInput
                                label={t('editors.attraction.artifactsPanel.title2')}
                                value={artifact.title}
                                onChange={(e) => patch(index, { title: e.currentTarget.value })}
                            />
                            <Textarea
                                label={t('editors.attraction.artifactsPanel.findHint')}
                                minRows={2}
                                value={artifact.findHint || ''}
                                onChange={(e) => patch(index, { findHint: e.currentTarget.value })}
                            />
                            <TextInput
                                label={t('editors.attraction.artifactsPanel.knownFor')}
                                value={artifact.knownFor || ''}
                                onChange={(e) => patch(index, { knownFor: e.currentTarget.value })}
                            />
                            <Textarea
                                label={t('editors.attraction.artifactsPanel.description')}
                                minRows={2}
                                value={artifact.description || ''}
                                onChange={(e) =>
                                    patch(index, { description: e.currentTarget.value })
                                }
                            />
                            <TextInput
                                label={t('editors.attraction.artifactsPanel.wikidataId')}
                                value={artifact.wikidataId || ''}
                                onChange={(e) =>
                                    patch(index, {
                                        wikidataId: e.currentTarget.value.trim() || null,
                                    })
                                }
                            />
                        </Stack>
                    ))}

                    {!artifacts.length && (
                        <Text size='sm' c='dimmed'>
                            {t('editors.attraction.artifactsPanel.empty')}
                        </Text>
                    )}

                    <Button
                        variant='light'
                        leftSection={<IconPlus size={16} />}
                        onClick={() => onChange([...artifacts, emptyArtifact(artifacts.length)])}
                    >
                        {t('editors.attraction.artifactsPanel.addArtifact')}
                    </Button>
                </Stack>
            </Section>
        </Stack>
    );
};
